import * as React from 'react';
import { useCheckoutPricing } from '@recurly/react-recurly';
import { buildQuoteInput } from '../utils/quote';

// Module level so the hook's effect sees the same handler every render. It
// re-runs (and re-prices) whenever the handler changes, so an inline one would
// reprice on every render. A pricing error just leaves no quote, and the page
// keeps its placeholder tax.
const ignoreError = () => {};

// Recurly's own tax and total for what Step 4 is selling to this address, as
// numbers, or null while there is nothing trustworthy to show. Null means:
//  - no whole postal code to price tax from (a quote with no address has $0 tax);
//  - the quote is still on its way, or the pricing call failed;
//  - a coupon is applied but the quote carries no discount. Recurly reports a
//    refused coupon (rate limit, bad code) as a plain quote without it, so
//    using it would put the full price next to a discounted First Box.
export default function useQuote({ planCode, coupon, country, postal }) {
  const input = React.useMemo(
    () => buildQuoteInput({ planCode, coupon, country, postal }),
    [planCode, coupon, country, postal],
  );

  // The first input mounts the hook; later ones go through setInput, once each.
  const [{ price, loading }, setInput] = useCheckoutPricing(input, ignoreError);
  const sent = React.useRef(JSON.stringify(input));
  React.useEffect(() => {
    const signature = JSON.stringify(input);
    if (signature === sent.current) return;
    sent.current = signature;
    setInput(input);
  }, [input, setInput]);

  // `price` is a fresh clone every render: read primitives off it so what this
  // returns is stable by value.
  const now = price && price.now;
  const taxes = now ? Number(now.taxes) : NaN;
  const total = now ? Number(now.total) : NaN;
  const discount = now ? Number(now.discount || 0) : 0;
  const ready = !loading && Boolean(input.address) && Number.isFinite(taxes) && Number.isFinite(total)
    && (!coupon || discount > 0);

  return React.useMemo(() => (ready ? { tax: taxes, total } : null), [ready, taxes, total]);
}
